// packages/web/src/components/Auth/LoginDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tab,
  Tabs,
  Box,
  Alert,
  Divider,
  Typography,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import GoogleIcon from '@mui/icons-material/Google';
import { useAuth } from '../../hooks/useAuth';
import { AuthService } from '@matchpoint/shared';

// Styled Components matching LandingPage exactly
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
    border: '1px solid #e5e5e5',
    margin: theme.spacing(1),
    maxWidth: '480px',
    width: 'calc(100% - 16px)',
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2, 1.5, 2),
}));

const DialogTitleText = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 700,
  color: '#1a1a1a',
  letterSpacing: '-0.01em',
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  minHeight: 40,
  '& .MuiTab-root': {
    fontSize: '0.9rem',
    fontWeight: 600,
    textTransform: 'none',
    color: '#666',
    minHeight: 40,
    letterSpacing: '-0.01em',
    flex: 1,
    padding: theme.spacing(1, 1.5),
  },
  '& .Mui-selected': {
    color: '#84b817',
  },
  '& .MuiTabs-indicator': {
    backgroundColor: '#c4d600',
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
}));

const GoogleButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#1a1a1a',
  fontSize: '0.9rem',
  fontWeight: 600,
  padding: theme.spacing(1.5, 2.5),
  borderRadius: '8px',
  border: '2px solid #e5e5e5',
  textTransform: 'none',
  width: '100%',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderColor: '#c4d600',
  },
  transition: 'all 0.2s ease',
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#c4d600',
  color: '#1a1a1a',
  fontSize: '0.9rem',
  fontWeight: 700,
  padding: theme.spacing(1.5, 3),
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(196, 214, 0, 0.25)',
  border: 'none',
  '&:hover': {
    backgroundColor: '#b3c200',
    boxShadow: '0 6px 16px rgba(196, 214, 0, 0.35)',
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    backgroundColor: '#e5e5e5',
    color: '#999',
    boxShadow: 'none',
  },
  transition: 'all 0.2s ease',
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  color: '#666',
  fontSize: '0.875rem',
  fontWeight: 600,
  padding: theme.spacing(1.5, 2),
  borderRadius: '8px',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '0.9rem',
    '& fieldset': {
      borderColor: '#e5e5e5',
    },
    '&:hover fieldset': {
      borderColor: '#c4d600',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#84b817',
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.9rem',
    color: '#666',
    '&.Mui-focused': {
      color: '#84b817',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: theme.spacing(1.5, 1.75),
  },
}));

const DividerText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: '#999',
  fontWeight: 400,
}));

const StyledLink = styled('button')(({ theme }) => ({
  fontSize: '0.9rem',
  color: '#84b817',
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  '&:hover': {
    color: '#679614',
    textDecoration: 'underline',
  },
}));

const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: '0.85rem',
  color: '#666',
  lineHeight: 1.5,
  fontWeight: 400,
}));

interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
}

export const LoginDialog: React.FC<LoginDialogProps> = ({
  open,
  onClose,
}) => {
  // Get auth methods directly from context
  const { signIn, signUp, signInWithGoogle } = useAuth();
  
  const [tab, setTab] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showEmailVerificationNotice, setShowEmailVerificationNotice] = useState(false);

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setName('');
    setError('');
    setSuccess('');
    setShowPasswordReset(false);
    setShowEmailVerificationNotice(false);
    setTab(0);
    onClose();
  };

  const handlePasswordReset = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const authService = AuthService.getInstance();
      const result = await authService.sendPasswordReset(email);
      if (result.success) {
        setSuccess(result.message);
      }
      setShowPasswordReset(false);
    } catch (error: any) {
      setError(error.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setShowEmailVerificationNotice(false);

    try {
      await signInWithGoogle();
      // Auth context will update automatically, close dialog
      handleClose();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setShowEmailVerificationNotice(false);

    try {
      if (showPasswordReset) {
        await handlePasswordReset();
        return;
      }

      if (tab === 0) {
        // Sign In
        await signIn(email, password);
        // Auth context handles state, just close dialog
        handleClose();
      } else {
        // Sign Up
        await signUp(email, password, name);
        setSuccess('Account created! Please check your email to verify your account before signing in.');
        // Switch to sign in tab but keep dialog open
        setTab(0);
        setPassword('');
        setName('');
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      
      if (tab === 0 && error.code === 'auth/email-not-verified') {
        setError(error.message);
        setShowEmailVerificationNotice(true);
      } else if (error.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters.');
      } else {
        setError(error.message || 'An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogHeader>
        <DialogTitleText>
          {showPasswordReset ? 'Reset Password' : 'Welcome to Sched.tennis'}
        </DialogTitleText>
      </DialogHeader>
      
      <DialogContent sx={{ px: 2, pb: 1.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {showEmailVerificationNotice && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                You need to verify your email before you can sign in.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                href="/verify-email"
                onClick={handleClose}
                sx={{ mt: 1 }}
              >
                Resend Verification Email
              </Button>
            </Box>
          </Alert>
        )}

        {showPasswordReset ? (
          <Box>
            <HelperText sx={{ mb: 2 }}>
              Enter your email address and we'll send you a link to reset your password.
            </HelperText>
            <StyledTextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
            />
          </Box>
        ) : (
          <>
            {/* Google Sign In */}
            <GoogleButton
              startIcon={<GoogleIcon />}
              onClick={handleGoogleSignIn}
              disabled={loading}
            >
              Continue with Google
            </GoogleButton>

            <Divider sx={{ my: 2 }}>
              <DividerText>
                or
              </DividerText>
            </Divider>

            {/* Tabs for Sign In / Sign Up */}
            <StyledTabs value={tab} onChange={(e, newValue) => setTab(newValue)}>
              <Tab label="Sign In" />
              <Tab label="Sign Up" />
            </StyledTabs>

            <form onSubmit={handleSubmit}>
              {tab === 1 && (
                <StyledTextField
                  fullWidth
                  label="Full Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  margin="normal"
                  required
                />
              )}

              <StyledTextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                autoComplete="email"
              />

              <StyledTextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                autoComplete={tab === 0 ? 'current-password' : 'new-password'}
              />

              {tab === 0 && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  <StyledLink
                    type="button"
                    onClick={() => setShowPasswordReset(true)}
                  >
                    Forgot your password?
                  </StyledLink>
                </Box>
              )}

              {tab === 1 && (
                <HelperText sx={{ mt: 1, display: 'block' }}>
                  By signing up, you'll receive a verification email. Please verify your account to complete registration.
                </HelperText>
              )}
            </form>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        {showPasswordReset ? (
          <>
            <SecondaryButton onClick={() => setShowPasswordReset(false)}>Back</SecondaryButton>
            <PrimaryButton
              onClick={handlePasswordReset}
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Email'}
            </PrimaryButton>
          </>
        ) : (
          <>
            <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={loading}
              onClick={handleSubmit}
            >
              {loading ? 'Loading...' : tab === 0 ? 'Sign In' : 'Sign Up'}
            </PrimaryButton>
          </>
        )}
      </DialogActions>
    </StyledDialog>
  );
};